import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent } from "react"
import { useEffect, useRef, useState } from "react"

import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from "@xyflow/react"

import type { EdgeProps } from "@xyflow/react"

import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

import { useWorkflowStore } from "../stores/workflow-store"

export function LabeledEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  data,
  selected,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  })

  const label = data?.label as string | undefined

  const [isEditing, setIsEditing] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [editValue, setEditValue] = useState(label ?? "")
  const inputRef = useRef<HTMLInputElement>(null)

  const updateEdgeData = useWorkflowStore((state) => state.updateEdgeData)
  const readOnly = useWorkflowStore((state) => state.readOnly)

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  useEffect(() => {
    setEditValue(label ?? "")
  }, [label])

  const handleDoubleClick = () => {
    if (readOnly) return
    setIsEditing(true)
  }

  const handleSave = () => {
    const trimmedValue = editValue.trim()
    updateEdgeData(id, { label: trimmedValue || undefined })
    setIsEditing(false)
  }

  const handleCancel = () => {
    setEditValue(label ?? "")
    setIsEditing(false)
  }

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      handleSave()
    } else if (event.key === "Escape") {
      event.preventDefault()
      handleCancel()
    }
  }

  const handleEdgePathDoubleClick = (event: ReactMouseEvent<SVGPathElement>) => {
    event.stopPropagation()
    handleDoubleClick()
  }

  const handleEdgeMouseEnter = () => {
    setIsHovered(true)
  }

  const handleEdgeMouseLeave = () => {
    setIsHovered(false)
  }

  const handleAddLabelClick = (event: ReactMouseEvent<HTMLDivElement>) => {
    if (readOnly || label) return
    event.stopPropagation()
    handleDoubleClick()
  }

  const handleBlur = () => {
    handleSave()
  }

  const isBooleanLabel = label === "true" || label === "false"
  const shouldShowAddLabel = !readOnly && !label && (isHovered || selected)

  const getLabelClassName = () => {
    if (label) {
      if (!isBooleanLabel) {
        return "bg-muted text-muted-foreground border border-transparent hover:bg-muted/80"
      }
      if (label === "true") {
        return "bg-success/15 border border-success text-foreground hover:bg-success/25"
      }
      return "bg-destructive/15 border border-destructive text-foreground hover:bg-destructive/25"
    }
    return cn(
      "flex size-6 items-center justify-center border border-transparent bg-muted/50 text-muted-foreground/50",
      shouldShowAddLabel ? "opacity-100" : "opacity-0",
      !readOnly && "hover:bg-muted/70 focus-visible:opacity-100"
    )
  }

  return (
    <>
      <BaseEdge
        id={id}
        markerEnd={markerEnd}
        path={edgePath}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          stroke: selected ? "hsl(var(--primary))" : style.stroke,
        }}
      />
      {/* Transparent hit area to make double-clicking the edge itself reliable */}
      <path
        className="react-flow__edge-interaction"
        d={edgePath}
        fill="none"
        stroke="transparent"
        onDoubleClick={handleEdgePathDoubleClick}
        onMouseEnter={handleEdgeMouseEnter}
        onMouseLeave={handleEdgeMouseLeave}
      />
      <EdgeLabelRenderer>
        {isEditing ? (
          <Input
            ref={inputRef}
            className="nodrag nopan border-border bg-background text-foreground absolute h-auto w-auto rounded px-2 py-0.5 text-[10px] font-medium shadow-none"
            type="text"
            value={editValue}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              minWidth: "60px",
              maxWidth: "150px",
              pointerEvents: "all",
            }}
            onBlur={handleBlur}
            onChange={(event) => setEditValue(event.target.value)}
            onKeyDown={handleKeyDown}
          />
        ) : (
          <div
            role={readOnly ? undefined : "button"}
            tabIndex={readOnly ? undefined : 0}
            title={!readOnly && !label ? "Click to add label" : undefined}
            className={cn(
              "nodrag nopan absolute cursor-pointer rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors transition-opacity",
              getLabelClassName(),
              readOnly
                ? "cursor-default"
                : "focus-visible:ring-ring inline-flex min-h-6 min-w-6 items-center justify-center focus-visible:ring-2 focus-visible:outline-none"
            )}
            style={{
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: "all",
            }}
            onClick={handleAddLabelClick}
            onDoubleClick={handleDoubleClick}
            onMouseEnter={handleEdgeMouseEnter}
            onMouseLeave={handleEdgeMouseLeave}
            onKeyDown={
              readOnly
                ? undefined
                : (event) => {
                    if (event.key !== "Enter" && event.key !== " ") return
                    event.preventDefault()
                    handleDoubleClick()
                  }
            }
          >
            {label || "+"}
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  )
}
